import { IBS_EARN_KEY_MAX_LENGTH, MATCH_VALUE_MAX_LENGTH, REGEX_VALUE_MAX_LENGTH } from '~/consts/luxLoyalty';

import type { MatchConfig } from '~/services/LuxLoyalty';

/**
 * Validates a matchConfig client-side, mirroring the server Zod rules
 * (svc-lux-loyalty matchConfigValidation.ts). ReDoS / safe-regex checks remain
 * server-side; here we only verify length caps and that regex patterns compile.
 *
 * @param config - The matchConfig to validate.
 * @returns A field-keyed error map; empty when the config is valid. Keys are
 *   `ibsEarnKey`, `match` (empty list), and `match.<index>.value`.
 */
export function validateMatchConfig(config: MatchConfig): Record<string, string> {
  const errors: Record<string, string> = {};

  const ibsEarnKey = config.ibsEarnKey?.trim() ?? '';
  if (!ibsEarnKey) {
    errors.ibsEarnKey = 'IBS Earn Key is required';
  } else if (config.ibsEarnKey.length > IBS_EARN_KEY_MAX_LENGTH) {
    errors.ibsEarnKey = `Must be at most ${IBS_EARN_KEY_MAX_LENGTH} characters`;
  }

  if (!config.match || config.match.length === 0) {
    errors.match = 'At least one condition is required';
    return errors;
  }

  config.match.forEach((condition, index) => {
    const key = `match.${index}.value`;
    const value = condition.value ?? '';
    if (!value) {
      errors[key] = 'Value is required';
      return;
    }
    if (condition.op === 'regex') {
      if (value.length > REGEX_VALUE_MAX_LENGTH) {
        errors[key] = `Regex must be at most ${REGEX_VALUE_MAX_LENGTH} characters`;
        return;
      }
      try {
        // Compile-validate so an obviously invalid pattern is caught before submit.
        new RegExp(value);
      } catch {
        errors[key] = 'Invalid regex pattern';
      }
    } else if (value.length > MATCH_VALUE_MAX_LENGTH) {
      errors[key] = `Must be at most ${MATCH_VALUE_MAX_LENGTH} characters`;
    }
  });

  return errors;
}
